/**
 * Ring membership vocabulary for the Geographic Explorer nearby-schools analysis.
 *
 * The Five-Year Enrollment Changes headline builds its baseline from the schools inside
 * the comparison-era isochrone and its current total from the schools inside today's
 * isochrone. Those two sets are not identical, so every school in the analysis belongs to
 * exactly one of the states below, and the detail table and its download report that state
 * so the per-school rows add back up to the headline percentages.
 */
export const RING_STATUS = {
  BOTH: 'both',
  ENTERED: 'entered',
  DEPARTED_OPEN: 'departed_open',
  DEPARTED_CLOSED: 'departed_closed',
};

/**
 * DEPARTED_CLOSED is inferred from a school reporting no students in the current year,
 * not from a status column: schools.status stays 'active' for years after a school
 * closes. It is reported ahead of ring membership, since a closed school has left the
 * area by definition and naming the closure is the more useful of the two facts.
 *
 * Commas are deliberately avoided: the nearby-schools export joins cells without quoting.
 */
export const RING_STATUS_LABELS = {
  [RING_STATUS.BOTH]: 'In area both years',
  [RING_STATUS.ENTERED]: 'New to area',
  [RING_STATUS.DEPARTED_OPEN]: 'Left area - still open',
  [RING_STATUS.DEPARTED_CLOSED]: 'Closed',
};

export const isDepartedRingStatus = (ringStatus) =>
  ringStatus === RING_STATUS.DEPARTED_OPEN || ringStatus === RING_STATUS.DEPARTED_CLOSED;

/**
 * NCES school-year labels behind the `comparison` and `current` enrollment buckets the
 * enrollment API returns (data_year 2019 and data_year >= 2024 for public schools).
 */
export const ENROLLMENT_BASELINE_LABEL = '2019-20';
export const ENROLLMENT_CURRENT_LABEL = '2024-25';

/**
 * Whether a school can carry a baseline comparison at all, mirroring
 * backend/app/utils/baseline_eligibility.py.
 *
 * A five-year change is only meaningful for a school that was open in the baseline year
 * and is still on the same campus. The nearby-schools rings are keyed on location, so a
 * school that has moved has its baseline ring around the campus it left; comparing that
 * area against today's would measure two different neighbourhoods and report the
 * difference as market change.
 */
export const BASELINE_STATUS = {
  ELIGIBLE: 'eligible',
  NOT_OPEN_BASELINE: 'not_open_2019',
  NO_BASELINE_LOCATION: 'no_2019_location',
  RELOCATED: 'relocated',
  NO_BASELINE_RING: 'no_baseline_ring',
};

// Shown wherever a baseline number would otherwise appear, so the absence reads as a
// deliberate answer rather than missing data.
export const BASELINE_STATUS_EXPLANATIONS = {
  [BASELINE_STATUS.NOT_OPEN_BASELINE]:
    `This school was not open in ${ENROLLMENT_BASELINE_LABEL}, so there is no baseline to compare against.`,
  [BASELINE_STATUS.NO_BASELINE_LOCATION]:
    `This school's ${ENROLLMENT_BASELINE_LABEL} location is unknown, so its area cannot be compared over time.`,
  [BASELINE_STATUS.RELOCATED]:
    `This school has moved since ${ENROLLMENT_BASELINE_LABEL}. Its former area covered different schools, so comparing the two would measure the move rather than the market.`,
  [BASELINE_STATUS.NO_BASELINE_RING]:
    `The ${ENROLLMENT_BASELINE_LABEL} drive-time area has not been processed for this campus, so there is no baseline to compare against.`,
};

export const BASELINE_STATUS_SHORT_LABELS = {
  [BASELINE_STATUS.NOT_OPEN_BASELINE]: `Not open in ${ENROLLMENT_BASELINE_LABEL}`,
  [BASELINE_STATUS.NO_BASELINE_LOCATION]: `${ENROLLMENT_BASELINE_LABEL} location unknown`,
  [BASELINE_STATUS.RELOCATED]: `Moved since ${ENROLLMENT_BASELINE_LABEL}`,
  [BASELINE_STATUS.NO_BASELINE_RING]: `No ${ENROLLMENT_BASELINE_LABEL} area available`,
};

/**
 * Resolve the baseline state for a drive time from the nearby-schools payload.
 *
 * `category` is the fallback for payloads with no baseline block (drop-a-pin locations,
 * or a cached response predating the field), where a new/newer school still has to be
 * excluded on its enrollment history alone.
 */
export const resolveBaselineStatus = (nearbySchools, selectedDriveTime, isNewOrNewer = false) => {
  const baseline = nearbySchools?.baseline;

  if (!baseline) {
    return isNewOrNewer ? BASELINE_STATUS.NOT_OPEN_BASELINE : BASELINE_STATUS.ELIGIBLE;
  }

  const forDriveTime = baseline.by_drive_time?.[selectedDriveTime]
    ?? baseline.by_drive_time?.[String(selectedDriveTime)];

  const status = forDriveTime?.status ?? baseline.status ?? BASELINE_STATUS.ELIGIBLE;

  // The backend does not read enrollment history the way categorizeSelectedSchool does,
  // so keep honouring a new/newer categorisation even when the backend says eligible.
  if (status === BASELINE_STATUS.ELIGIBLE && isNewOrNewer) {
    return BASELINE_STATUS.NOT_OPEN_BASELINE;
  }

  return status;
};

export const hasBaselineComparison = (nearbySchools, selectedDriveTime, isNewOrNewer = false) =>
  resolveBaselineStatus(nearbySchools, selectedDriveTime, isNewOrNewer) === BASELINE_STATUS.ELIGIBLE;
